import React, { useEffect, useState } from 'react'
import { useRoleStore } from '@store'
import { Plus, Eye, Edit2, Trash2, Copy, RotateCcw, Shield, Users, Filter, ArrowUpDown } from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader } from '@components/common'
import RoleForm from './RoleForm'
import RoleView from './RoleView'
import './Roles.css'

/**
 * Roles Management Page (Super Admin Only)
 *
 * Complete role management interface with:
 * - List view with DataTable
 * - Search and filtering
 * - Create/Edit/View/Delete/Clone operations
 * - Statistics dashboard
 * - System vs Custom roles handling
 *
 * Features:
 * - Full CRUD operations
 * - Real-time search
 * - Type filtering (System/Custom)
 * - Status filtering (Active/Archived)
 * - Clone functionality
 * - System role protection (cannot edit/delete)
 */
const Roles = () => {
	const {
		roles,
		loading,
		filters,
		pagination,
		stats,
		fetchRoles,
		selectRole,
		deleteRole,
		restoreRole,
		cloneRole,
		setSearch,
		setTypeFilter,
		setStatusFilter,
		nextPage,
		prevPage,
	} = useRoleStore()

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [actionType, setActionType] = useState(null) // 'delete', 'restore', 'clone'
	const [selectedRoleForAction, setSelectedRoleForAction] = useState(null)
	const [cloneData, setCloneData] = useState(null)

	// Fetch roles on mount
	useEffect(() => {
		fetchRoles()
	}, [])

	/**
	 * Handle create role
	 */
	const handleCreate = () => {
		selectRole(null)
		setShowCreateModal(true)
	}

	/**
	 * Handle view role
	 */
	const handleView = (role) => {
		selectRole(role)
		setShowViewModal(true)
	}

	/**
	 * Handle edit role
	 */
	const handleEdit = (role) => {
		selectRole(role)
		setShowEditModal(true)
	}

	/**
	 * Handle delete role
	 */
	const handleDelete = (role) => {
		setSelectedRoleForAction(role)
		setActionType('delete')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle restore role
	 */
	const handleRestore = (role) => {
		setSelectedRoleForAction(role)
		setActionType('restore')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle clone role
	 */
	const handleClone = (role) => {
		// Pre-fill clone data with modified name and company_id
		// Backend will auto-generate the slug
		const clonedData = {
			name: `${role.name} (Copy)`,
			company_id: 1, // TODO: Get from logged-in user's context
		}
		setCloneData(clonedData)
		setSelectedRoleForAction(role)
		setActionType('clone')
		setShowConfirmDialog(true)
	}

	/**
	 * Confirm action (delete/restore/clone)
	 */
	const confirmAction = async () => {
		if (!selectedRoleForAction) return

		try {
			if (actionType === 'delete') {
				await deleteRole(selectedRoleForAction.id)
			} else if (actionType === 'restore') {
				await restoreRole(selectedRoleForAction.id)
			} else if (actionType === 'clone') {
				await cloneRole(selectedRoleForAction.id, cloneData)
			}

			setShowConfirmDialog(false)
			setSelectedRoleForAction(null)
			setActionType(null)
			setCloneData(null)
		} catch (error) {
			console.error('Action failed:', error)
		}
	}

	/**
	 * Cancel action
	 */
	const cancelAction = () => {
		setShowConfirmDialog(false)
		setSelectedRoleForAction(null)
		setActionType(null)
		setCloneData(null)
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = (status, deletedAt) => {
		if (deletedAt || status === 'archived') {
			return <span className="badge badge--archived">Archived</span>
		}
		return <span className="badge badge--active">Active</span>
	}

	/**
	 * Get type badge
	 */
	const getTypeBadge = (isSystemDefault) => {
		// Handle both boolean and numeric values (1/0 from database)
		if (isSystemDefault === true || isSystemDefault === 1 || isSystemDefault === '1') {
			return <span className="badge badge--primary">System</span>
		}
		return <span className="badge badge--info">Custom</span>
	}

	// Define table columns
	const columns = [
		{
			key: 'name',
			label: 'Role',
			sortable: true,
			render: (role) => (
				<div className="role-cell">
					<div className="role-cell__name">{role.name}</div>
					<div className="role-cell__slug">{role.slug}</div>
				</div>
			),
		},
		{
			key: 'type',
			label: 'Type',
			sortable: true,
			align: 'center',
			render: (role) => getTypeBadge(role.is_system_default),
		},
		{
			key: 'permissions',
			label: 'Permissions',
			align: 'center',
			render: (role) => {
				const permCount = role.permissions ? Object.keys(role.permissions).length : 0
				return `${permCount} resources`
			},
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			render: (role) => getStatusBadge(role.status, role.deleted_at),
		},
	]

	// Define table actions
	const actions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: handleEdit,
			show: (role) => !role.deleted_at && role.status !== 'archived',
		},
		{
			label: 'Clone',
			icon: <Copy size={16} />,
			onClick: handleClone,
			variant: 'info',
			show: (role) => !role.deleted_at && role.status !== 'archived',
		},
		{
			label: 'Delete',
			icon: <Trash2 size={16} />,
			onClick: handleDelete,
			variant: 'danger',
			show: (role) => !role.is_system_default && !role.deleted_at && role.status !== 'archived',
		},
		{
			label: 'Restore',
			icon: <RotateCcw size={16} />,
			onClick: handleRestore,
			variant: 'primary',
			show: (role) => role.deleted_at || role.status === 'archived',
		},
	]

	return (
		<div className="roles-page">
			{/* Page Header with Breadcrumbs */}
			<PageHeader
				title="User Roles"
				description="Manage user roles and permissions"
				breadcrumbs={[{ label: 'User Roles' }]}
				actions={
					<Button variant="primary" onClick={handleCreate}>
						<Plus size={20} />
						<span>Add Role</span>
					</Button>
				}
			/>

			{/* Statistics */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--primary">
						<Shield size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Total Roles</div>
						<div className="stat-card__value">{stats.total}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--success">
						<Users size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">System Roles</div>
						<div className="stat-card__value">{stats.system}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--info">
						<Copy size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Custom Roles</div>
						<div className="stat-card__value">{stats.custom}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--secondary">
						<Filter size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Archived</div>
						<div className="stat-card__value">{stats.archived}</div>
					</div>
				</div>
			</div>

			{/* Filters & Search */}
			<div className="filters-section">
				<SearchBar
					value={filters.search}
					onChange={setSearch}
					placeholder="Search roles..."
					loading={loading}
				/>

				<div className="filters-section__divider">
					<Filter size={18} />
				</div>

				<div className="filters-section__controls">
					<Select
						value={filters.type}
						onChange={(e) => setTypeFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Types' },
							{ value: 'system', label: 'System Roles' },
							{ value: 'custom', label: 'Custom Roles' },
						]}
					/>

					<div className="filters-section__sort-icon">
						<ArrowUpDown size={18} />
					</div>

					<Select
						value={filters.status}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'active', label: 'Active' },
							{ value: 'archived', label: 'Archived' },
						]}
					/>
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={roles}
				actions={actions}
				loading={loading}
				emptyMessage="No roles found. Create your first custom role to get started."
				onRowClick={handleView}
			/>

			{/* Pagination */}
			{pagination.totalPages > 1 && (
				<div className="pagination">
					<div className="pagination__info">
						Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
					</div>
					<div className="pagination__controls">
						<Button
							variant="secondary"
							onClick={prevPage}
							disabled={pagination.page === 1 || loading}
							size="small"
						>
							Previous
						</Button>
						<Button
							variant="secondary"
							onClick={nextPage}
							disabled={pagination.page >= pagination.totalPages || loading}
							size="small"
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* Create Modal */}
			{showCreateModal && <RoleForm mode="create" onClose={() => setShowCreateModal(false)} />}

			{/* Edit Modal */}
			{showEditModal && <RoleForm mode="edit" onClose={() => setShowEditModal(false)} />}

			{/* View Modal */}
			{showViewModal && (
				<RoleView
					roleData={roles.find((r) => r.id === useRoleStore.getState().selectedRole?.id)}
					onClose={() => setShowViewModal(false)}
					onEdit={() => {
						setShowViewModal(false)
						setShowEditModal(true)
					}}
					onClone={(role) => {
						setShowViewModal(false)
						handleClone(roles.find((r) => r.id === useRoleStore.getState().selectedRole?.id))
					}}
					onDelete={() => {
						setShowViewModal(false)
						handleDelete(roles.find((r) => r.id === useRoleStore.getState().selectedRole?.id))
					}}
				/>
			)}

			{/* Confirm Dialog */}
			{showConfirmDialog && selectedRoleForAction && (
				<ConfirmDialog
					isOpen={true}
					onConfirm={confirmAction}
					onCancel={cancelAction}
					title={
						actionType === 'delete'
							? 'Delete Role?'
							: actionType === 'restore'
								? 'Restore Role?'
								: 'Clone Role?'
					}
					message={
						actionType === 'delete'
							? `Are you sure you want to delete "${selectedRoleForAction.name}"? This will archive it but data will be preserved.`
							: actionType === 'restore'
								? `Are you sure you want to restore "${selectedRoleForAction.name}"? This will make it active again.`
								: `Clone "${selectedRoleForAction.name}" as "${cloneData?.name}"? This will create a new custom role with the same permissions.`
					}
					confirmText={actionType === 'delete' ? 'Delete' : actionType === 'restore' ? 'Restore' : 'Clone'}
					variant={actionType === 'delete' ? 'danger' : actionType === 'restore' ? 'info' : 'primary'}
				/>
			)}
		</div>
	)
}

export default Roles
